import type { KeyboardEvent, ReactNode } from 'react';

export interface PanelTab<T extends string> {
  readonly id: T;
  readonly label: string;
  /** A count the caller knows; left out, never drawn as 0, when it does not. */
  readonly count?: number;
}

/** The ids a tab and its panel share, so `aria-controls` and `aria-labelledby` agree. */
export function panelTabIds(prefix: string, id: string): { tab: string; panel: string } {
  return { tab: `${prefix}-tab-${id}`, panel: `${prefix}-panel-${id}` };
}

/**
 * THE TABS OF A PANEL — Breakdown, Execution, Resources on the scene breakdown's right.
 *
 * A WAI-ARIA tab list: ←/→ move between tabs, Home/End go to the ends, and the selected tab is
 * the only one in the tab order. ONE TAB IS NO TABS: the strip is not drawn at all, so a panel
 * with a single view never shows a control that switches to nothing.
 */
export function PanelTabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  idPrefix,
  testIdPrefix,
  end,
}: {
  tabs: readonly PanelTab<T>[];
  value: T;
  onChange: (next: T) => void;
  label: string;
  idPrefix: string;
  testIdPrefix: string;
  /** A control after the tabs (a link to a wider view). */
  end?: ReactNode;
}) {
  if (tabs.length <= 1) return null;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const at = tabs.findIndex((t) => t.id === value);
    let next: number | null = null;
    if (event.key === 'ArrowRight') next = (at + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (at - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next === null) return;

    const tab = tabs[next];
    if (tab === undefined) return;
    event.preventDefault();
    onChange(tab.id);
    event.currentTarget.ownerDocument.getElementById(panelTabIds(idPrefix, tab.id).tab)?.focus();
  };

  return (
    <div className="ui-tabs" data-testid={testIdPrefix}>
      <div className="ui-tabs-list" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
        {tabs.map((tab) => {
          const ids = panelTabIds(idPrefix, tab.id);
          const selected = tab.id === value;

          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={ids.tab}
              aria-selected={selected}
              aria-controls={ids.panel}
              tabIndex={selected ? 0 : -1}
              className="ui-tab"
              data-testid={`${testIdPrefix}-${tab.id}`}
              onClick={() => {
                onChange(tab.id);
              }}
            >
              <span>{tab.label}</span>
              {tab.count === undefined ? null : <span className="ui-tab-count">{String(tab.count)}</span>}
            </button>
          );
        })}
      </div>
      {end === undefined ? null : <div className="ui-tabs-end">{end}</div>}
    </div>
  );
}
